/**
 * IneffectiveBreakdownSection — the "Tiempo perdido" (ineffective-time
 * breakdown) block, moved verbatim out of `MatchAnalytics.tsx` (PMA-COCK-189,
 * client wave 2026-09-13: "se podria dejar de ultimo? es buena informacion,
 * pero si se queda de ultimo seria mejor, igual en el PDF"). Same inner
 * markup, same testids, same `ineffective_breakdown` gate as before —
 * `MatchAnalytics` no longer renders this block at all; both mount sites
 * (`AnalyticsView`, `PublicMatchAnalytics`) render it themselves, last.
 *
 * The block used to be a child of `MatchAnalytics`'s own themed stats-panel
 * card (`STATS_THEME_DARK` background) — as a standalone, top-level section
 * it now provides that same card wrapper itself. The theme tokens and the
 * clock formatter are intentionally duplicated from `MatchAnalytics.tsx`
 * rather than imported: several existing tests (`AnalyticsView.*.test.tsx`)
 * `vi.mock` that whole module down to a bare stub, and any real import from
 * it would crash the moment this component renders unmocked underneath —
 * same "duplicating a small, rarely-changing value is lower risk than a new
 * cross-module import" call this codebase already makes for `LIVE_STATUSES`
 * (`AnalyticsView.tsx` / `PublicMatchAnalytics.tsx`). If `MatchAnalytics`'s
 * `ACTIVE_STATS_THEME` ever flips to `STATS_THEME_LIGHT`, update the literal
 * values below to match.
 */
import {
  computeIneffectiveActionRows,
  computeIneffectiveTotals,
} from "../../utils/ineffectiveRows";
import SectionGate from "../../../../components/match/SectionGate";
import type {
  ShareSectionKey,
  ShareUpsell,
  ShareVisibilityState,
} from "../../../../lib/shareVisibility";
import type { IneffectiveBreakdown } from "../../utils";
import type { Match } from "../../types";
import type { ReactNode } from "react";

// Duplicated from MatchAnalytics.tsx's STATS_THEME_DARK (the file's own
// ACTIVE_STATS_THEME pick) — see the module comment above for why this
// isn't a shared import.
const THEME = {
  panel: "bg-slate-950 text-white",
  label: "text-emerald-300",
  valueAlt: "text-white",
  subtle: "text-slate-300",
  box: "bg-black",
  border: "border-slate-700",
  totalRow: "bg-emerald-500/25",
};

// Duplicated from MatchAnalytics.tsx's formatSecondsAsClock — see the
// module comment above for why this isn't a shared import.
const formatSecondsAsClock = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

interface IneffectiveBreakdownSectionProps {
  match: Match;
  ineffectiveBreakdown?: IneffectiveBreakdown | null;
  /**
   * Accepted for prop-shape parity with `MatchAnalytics`'s own signature
   * (both molecules are mounted side by side from the same callers, each
   * already holding these three values). Unused here: this block's figures
   * come entirely from `ineffectiveBreakdown`, exactly as they did inside
   * `MatchAnalytics` before this wave.
   */
  ineffectiveSeconds?: number;
  varTimeSeconds?: number;
  timeoutSeconds?: number;
  sectionState?: (key: ShareSectionKey) => ShareVisibilityState;
  upsell?: ShareUpsell;
  language?: "en" | "es";
  t: (key: string, fallback?: string) => string;
}

export default function IneffectiveBreakdownSection(
  props: IneffectiveBreakdownSectionProps,
) {
  const { match, ineffectiveBreakdown, sectionState, upsell, language, t } =
    props;

  const gate = (key: ShareSectionKey, node: ReactNode): ReactNode => {
    if (!sectionState || !upsell || !language) return node;
    return (
      <SectionGate
        sectionKey={key}
        state={sectionState(key)}
        upsell={upsell}
        language={language}
      >
        {node}
      </SectionGate>
    );
  };

  const rows = computeIneffectiveActionRows(ineffectiveBreakdown, t);
  const totals = computeIneffectiveTotals(ineffectiveBreakdown);

  return (
    <>
      {gate(
        "ineffective_breakdown",
        <div
          className={`rounded-2xl p-4 sm:p-5 md:p-6 shadow-xl ${THEME.panel}`}
        >
          <div data-testid="analytics-ineffective-breakdown">
            {/* Section header */}
            <div
              className={`text-sm uppercase tracking-wide mb-3 ${THEME.label}`}
            >
              {t("analytics.tiempoPerdido", "Tiempo perdido")}
            </div>

            {/* Column header: HOME | — | AWAY */}
            <div
              className={`grid grid-cols-[1fr_minmax(100px,1.4fr)_1fr] text-xs uppercase tracking-wider mb-1 px-1 ${THEME.subtle}`}
            >
              <div className="text-left">{match.home_team.short_name}</div>
              <div className="text-center">—</div>
              <div className="text-right">{match.away_team.short_name}</div>
            </div>

            {/* Standalone rows: Goal and VAR */}
            {rows
              .filter((row) => row.action === "Goal" || row.action === "VAR")
              .map((row) => (
                <div
                  key={row.action}
                  className={`grid grid-cols-[1fr_minmax(100px,1.4fr)_1fr] items-center border-b px-1 py-2 ${THEME.border}`}
                  data-testid={`stat-ineffective-${row.action.toLowerCase()}`}
                >
                  <div
                    className={`text-left font-mono text-sm ${THEME.valueAlt}`}
                  >
                    <span data-testid="stat-breakdown-home">
                      {formatSecondsAsClock(row.home)}
                    </span>
                  </div>
                  <div className={`text-center text-sm ${THEME.subtle}`}>
                    {row.label}
                    {row.neutral > 0 && (
                      <span className={`block text-xs mt-0.5 ${THEME.subtle}`}>
                        {formatSecondsAsClock(row.neutral)}{" "}
                        {t("analytics.neutral", "Neutral")}
                      </span>
                    )}
                  </div>
                  <div
                    className={`text-right font-mono text-sm ${THEME.valueAlt}`}
                  >
                    <span data-testid="stat-breakdown-away">
                      {formatSecondsAsClock(row.away)}
                    </span>
                  </div>
                </div>
              ))}

            {/* "Tiempo perdido" grouped rows */}
            <div className={`mt-3 rounded-lg overflow-hidden ${THEME.box}`}>
              {rows
                .filter((row) => row.action !== "Goal" && row.action !== "VAR")
                .map((row, idx, arr) => (
                  <div
                    key={row.action}
                    className={`grid grid-cols-[1fr_minmax(100px,1.4fr)_1fr] items-center px-3 py-2 ${
                      idx < arr.length - 1 ? `border-b ${THEME.border}` : ""
                    }`}
                    data-testid={`stat-ineffective-${row.action.toLowerCase()}`}
                  >
                    <div
                      className={`text-left font-mono text-sm ${THEME.valueAlt}`}
                    >
                      <span data-testid="stat-breakdown-home">
                        {formatSecondsAsClock(row.home)}
                      </span>
                    </div>
                    <div className={`text-center text-sm ${THEME.subtle}`}>
                      {row.label}
                      {row.neutral > 0 && (
                        <span
                          className={`block text-xs mt-0.5 ${THEME.subtle}`}
                        >
                          {formatSecondsAsClock(row.neutral)}{" "}
                          {t("analytics.neutral", "Neutral")}
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-right font-mono text-sm ${THEME.valueAlt}`}
                    >
                      <span data-testid="stat-breakdown-away">
                        {formatSecondsAsClock(row.away)}
                      </span>
                    </div>
                  </div>
                ))}

              {/* Total row */}
              <div
                data-testid="stat-ineffective-total"
                className={`grid grid-cols-[1fr_minmax(100px,1.4fr)_1fr] items-center px-3 py-2 border-t ${THEME.totalRow} ${THEME.border}`}
              >
                <div
                  className={`text-left font-mono text-sm font-bold ${THEME.valueAlt}`}
                >
                  {formatSecondsAsClock(totals.home)}
                </div>
                <div
                  className={`text-center text-sm font-bold ${THEME.valueAlt}`}
                >
                  {t("analytics.ineffectiveTotals", "Total")}
                  {totals.neutral > 0 && (
                    <span
                      className={`block text-xs font-normal mt-0.5 ${THEME.subtle}`}
                    >
                      {formatSecondsAsClock(totals.neutral)}{" "}
                      {t("analytics.neutral", "Neutral")}
                    </span>
                  )}
                </div>
                <div
                  className={`text-right font-mono text-sm font-bold ${THEME.valueAlt}`}
                >
                  {formatSecondsAsClock(totals.away)}
                </div>
              </div>
            </div>
          </div>
        </div>,
      )}
    </>
  );
}
